import type { Address } from "viem"
import { computed, makeObservable, observable, runInAction } from "mobx"

import type { IncentiveStoreDAO } from "@/domains/incentive"
import type { TokenStore } from "@/domains/tokens"
import { LcAddress } from "@/domains/core"
import { IncentiveStore } from "@/domains/incentive"

// Import the interface type
import type { GaugeStoreDAO, IGauge, IRevenueSource } from "./interfaces"

const sanitizeNumber = (value: number): number => {
  if (Number.isNaN(value)) {
    return 0
  }
  if (value === Infinity) {
    return 0
  }
  return value
}

export class GaugeStore implements IGauge {
  // Core gauge data
  address: LcAddress
  revenueSourceAddress: LcAddress
  revenueSource: IRevenueSource | undefined = undefined
  bribeAddress: LcAddress
  apr: string
  aprLastEpoch: string
  rewardRate: string
  totalBribeValueUsd: string
  totalSupply: string
  votes: number
  votesPercent: number
  incentives: Array<IncentiveStore>

  constructor(data: GaugeStoreDAO) {
    this.address = data.address
    this.revenueSourceAddress = data.revenueSourceAddress
    this.bribeAddress = data.bribeAddress
    this.apr = data.apr
    this.aprLastEpoch = data.aprLastEpoch
    this.rewardRate = data.rewardRate
    this.totalBribeValueUsd = data.totalBribeValueUsd
    this.totalSupply = data.totalSupply
    this.votes = Number(data.votes)
    this.votesPercent = Number(data.votesPercent)
    this.incentives = []

    makeObservable(this, {
      address: observable,
      revenueSourceAddress: observable,
      revenueSource: observable,
      bribeAddress: observable,
      apr: observable,
      aprLastEpoch: observable,
      rewardRate: observable,
      totalBribeValueUsd: observable,
      totalSupply: observable,
      votes: observable,
      votesPercent: observable,
      incentives: observable,
      tokens: computed,
      totalIncentiveValueUsd: computed,
    })
  }

  connectToRevenueSource(source: IRevenueSource): void {
    runInAction(() => {
      this.revenueSource = source
    })
  }

  updateVoteData(votes: string, votesPercent: string): void {
    runInAction(() => {
      this.votes = Number(votes)
      this.votesPercent = Number(votesPercent)
    })
  }

  updateStaticData(data: Partial<GaugeStoreDAO>): void {
    runInAction(() => {
      if (data.apr !== undefined) this.apr = data.apr
      if (data.aprLastEpoch !== undefined) this.aprLastEpoch = data.aprLastEpoch
      if (data.rewardRate !== undefined) this.rewardRate = data.rewardRate
      if (data.totalBribeValueUsd !== undefined) this.totalBribeValueUsd = data.totalBribeValueUsd
      if (data.totalSupply !== undefined) this.totalSupply = data.totalSupply
      if (data.votes !== undefined) this.votes = Number(data.votes)
      if (data.votesPercent !== undefined) this.votesPercent = Number(data.votesPercent)
      if (data.revenueSourceAddress !== undefined) this.revenueSourceAddress = data.revenueSourceAddress
      // Note: incentives are updated via updateIncentives method, not here
    })
  }

  /**
   * Update incentives from DAO array
   */
  updateIncentives(daoIncentives: Array<IncentiveStoreDAO>): void {
    runInAction(() => {
      this.incentives = daoIncentives.map((dao) => new IncentiveStore(dao))
    })
  }

  /**
   * Get tokens from the connected revenue source, or empty array if not connected
   * This provides a safe fallback for rendering when revenueSource is undefined
   */
  get tokens(): Array<TokenStore> {
    return this.revenueSource?.tokens ?? []
  }

  /**
   * Computed total incentive value in USD
   * Sums all incentive rewardAmountUsd values reactively
   * Updates automatically when incentives change or token prices change
   */
  get totalIncentiveValueUsd(): number {
    return this.incentives.reduce((sum, incentive) => {
      return sum + incentive.rewardAmountUsd
    }, 0)
  }
}
